import type { App } from "@aiyo/shared";
import { ChevronRight } from "@gravity-ui/icons";
import { Button } from "@heroui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { ConfirmDialog } from "../../components/dialogs";
import {
  Code,
  CodeBlock,
  CopyButton,
  DangerZone,
  InlineError,
  KeyValue,
  LoadingBlock,
  Panel,
} from "../../components/ui";
import { PUBLIC_API_URL } from "../../lib/config";
import { useFormat } from "../../lib/format";
import { browserTimeZone } from "../../lib/hooks";
import { useDeleteApp, useOverview, useUpdateApp } from "../../lib/queries";

export function OverviewTab({ app, canEdit, isOwner }: { app: App; canEdit: boolean; isOwner: boolean }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const update = useUpdateApp(app.id);
  const remove = useDeleteApp();
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const register = `curl -X PUT ${PUBLIC_API_URL}/v1/customers/user-123 \\
  -H "X-Api-Key: ak_live_..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "attributes": { "name": "Erry" },
    "subscriptions": [
      { "channel": "email", "address": "user@example.com" },
      { "channel": "fcm", "address": "<firebase-installation-id>", "credential": "fcm-main" }
    ]
  }'`;
  const send = `curl -X POST ${PUBLIC_API_URL}/v1/messages \\
  -H "X-Api-Key: ak_live_..." \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: order-123-shipped" \\
  -d '{
    "to": { "customer_ids": ["user-123"] },
    "priority": "high",
    "channels": [
      { "type": "fcm", "payload": { "title": "Order shipped", "body": "Order #123 is on its way" } },
      { "type": "email", "payload": { "subject": "Order #123 shipped", "text": "Your order is on its way." } }
    ]
  }'`;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <div className="flex min-w-0 flex-col gap-6">
        <Activity appId={app.id} />

        <Panel title={t("appDetail.details")}>
          <KeyValue
            items={[
              {
                label: t("appDetail.appId"),
                value: (
                  <span className="flex items-center gap-1">
                    <Code>{app.id}</Code>
                    <CopyButton value={app.id} />
                  </span>
                ),
              },
              { label: t("apps.slug"), value: <Code>{app.slug}</Code> },
              { label: t("common.status"), value: t(`status.app.${app.status}`) },
              { label: t("common.created"), value: fmt.dateTime(app.created_at) },
              { label: t("common.updated"), value: fmt.dateTime(app.updated_at) },
            ]}
          />
        </Panel>

        {canEdit ? (
          <Panel
            title={app.status === "active" ? t("apps.suspend") : t("apps.activate")}
            description={t("apps.suspendedHint")}
          >
            <div className="flex flex-col gap-4">
              <div>
                <Button
                  variant="secondary"
                  isPending={update.isPending}
                  onPress={() => update.mutate({ status: app.status === "active" ? "suspended" : "active" })}
                >
                  {app.status === "active" ? t("apps.suspend") : t("apps.activate")}
                </Button>
              </div>
              <InlineError error={update.error} />
            </div>
          </Panel>
        ) : null}

        {isOwner ? (
          <DangerZone title={t("apps.dangerZone")} description={t("apps.deleteHint")}>
            <Button variant="danger-soft" onPress={() => setConfirmDelete(true)}>
              {t("common.delete")}
            </Button>
          </DangerZone>
        ) : null}
      </div>

      <Panel
        title={t("appDetail.quickStart")}
        description={t("appDetail.quickStartHint")}
        className="self-start"
      >
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t("appDetail.step1")}</span>
          <CodeBlock value={register} />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">{t("appDetail.step2")}</span>
          <CodeBlock value={send} />
        </div>
      </Panel>

      <ConfirmDialog
        isOpen={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("apps.deleteTitle")}
        body={t("apps.deleteBody")}
        confirmLabel={t("common.delete")}
        isPending={remove.isPending}
        error={remove.error}
        onConfirm={() =>
          remove.mutate(app.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              navigate("/apps", { replace: true });
            },
          })
        }
      />
    </div>
  );
}

/** Last 7 days of this app, with links to the views already filtered to it. */
function Activity({ appId }: { appId: string }) {
  const { t, i18n } = useTranslation();
  const tz = useMemo(browserTimeZone, []);
  const overview = useOverview({ app_id: appId, days: 7, tz });
  const pct = new Intl.NumberFormat(i18n.resolvedLanguage === "id" ? "id-ID" : "en-US", {
    style: "percent",
    maximumFractionDigits: 1,
  });
  const o = overview.data;
  const settled = o ? o.totals.total - o.totals.in_progress : 0;
  const reached = o && settled > 0 ? (o.totals.delivered + o.totals.accepted) / settled : null;
  const links = [
    { to: `/?app=${appId}`, label: t("appDetail.openOverview") },
    { to: `/logs?app=${appId}`, label: t("appDetail.openLog") },
    { to: `/customers?app=${appId}`, label: t("appDetail.openCustomers") },
  ];

  return (
    <Panel title={t("appDetail.activity")}>
      {overview.isPending ? (
        <LoadingBlock />
      ) : o ? (
        <div className="grid grid-cols-3 gap-3">
          <Figure label={t("overview.kpi.deliveries")} value={String(o.totals.total)} />
          <Figure label={t("overview.kpi.reached")} value={reached === null ? "—" : pct.format(reached)} />
          <Figure
            label={t("overview.kpi.problems")}
            value={String(o.totals.problems)}
            danger={o.totals.problems > 0}
          />
        </div>
      ) : null}
      <nav className="-mx-2 flex flex-col">
        {links.map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="group flex items-center justify-between rounded-xl px-2 py-2 text-sm outline-none hover:bg-default focus-visible:ring-2 focus-visible:ring-focus"
          >
            {l.label}
            <ChevronRight className="size-4 text-muted transition-transform group-hover:translate-x-0.5" />
          </Link>
        ))}
      </nav>
    </Panel>
  );
}

function Figure({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl bg-surface-secondary p-3">
      <span className="text-xs text-muted">{label}</span>
      <span className={`text-lg font-semibold ${danger ? "text-danger" : ""}`}>{value}</span>
    </div>
  );
}
